import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  CountryIso2,
  CountrySelector,
  defaultCountries,
  parseCountry,
} from 'react-international-phone';
import phone from 'phone';
import AuthHeaderLayout from './components/AuthHeaderLayout';
import GoogleButton, { GoogleProfile } from './components/GoogleButton';
import FacebookButton, { FacebookProfile } from './components/FacebookButton';
import VerifyOTP from './components/VerifyOTP';
import AppleButton from './components/AppleButton';
import { ProfileType } from './types';
import LabelTextInput from '../Global/LabelTextInput';
import TabSwitch from '../TabSwitch';
import { TabSwitchDefaultTabs } from '../TabSwitch/types';
import { validateEmail } from '../../helpers/validators';
import { LOGIN_VIA, STORAGE_KEYS } from '../../helpers/config';
import { onlyNumbers, storageListener } from '../../helpers';
import './react-international-phone.css';
import { ExclamationSquare } from '../Global/icons';
import Account_Message from './components/Messages';
import './styles.css';
import { ReduxStateType } from '../../redux/store';
import { useSelector } from 'react-redux';
import Close from '../Global/icons/Close';
import { allowedCountries } from '../../settings.json';
const faSpinnerIcon = faSpinner as IconProp;

interface Proptypes {
  onSubmit?: (
    data: ProfileType,
    onError: (message: string) => void,
    onSuccess: Function
  ) => void;
  registerGoogle?: (
    data: {
      social_id: string;
      name?: string;
      email: string;
      first_name?: string;
      last_name?: string;
      email_verified: boolean;
      login_via?: number;
    },
    onError: (message: string) => void,
    onSuccess: Function
  ) => void;
  prefillData?: string;
  prefillCountryCode?: string;
  prefillCountryISO?: string;
  errorMessage?: string;
  editLoginMethod?: boolean;
  overrideSubmit?: boolean;
  onClose?: () => void;
}

const Register = ({
  onSubmit,
  registerGoogle,
  prefillData,
  prefillCountryCode,
  prefillCountryISO,
  errorMessage,
  editLoginMethod = false,
  overrideSubmit,
  onClose,
}: Proptypes) => {
  const [continueEmail, setContinueEmail] = useState(
    prefillData && !editLoginMethod ? true : false
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(false);
  const [loading, setMainLoading] = useState(false);
  const user = useSelector((state: ReduxStateType) => state.user);
  const [email, setEmail] = useState('');
  const [activeTab, setActiveTab] = useState<TabSwitchDefaultTabs['tabs']>(
    'Email'
  );
  const [verifyOTP, setVerifyOTP] = useState(false);
  const [countryCode, setCountryCode] = useState({
    dialCode: '1',
    country: 'us' as CountryIso2,
  });
  // Phone Number and Validation
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const countries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return allowedCountries.includes(iso2);
  });
  const registerRedirect = (profileObj: GoogleProfile | FacebookProfile) => {
    setIsProcessing(false);
    const formData = {
      social_id:
        // @ts-ignore
        profileObj.googleId || profileObj.facebookId || profileObj.userID,
      email: profileObj.email?.length > 0 ? profileObj.email : email,
      email_verified: profileObj.email ? true : false,
      login_via: profileObj.login_via ?? LOGIN_VIA.google,
    };
    try {
      registerGoogle(
        formData,
        (error) => {},
        () => {}
      );
    } catch (error) {
      //
    }
  };

  useEffect(() => {
    if (prefillData && !errorMessage) {
      !editLoginMethod && setContinueEmail(true);
      if (prefillCountryCode && prefillCountryISO) {
        setPhoneNumber(prefillData);
        const countryData = defaultCountries.filter((dc) => {
          const c = parseCountry(dc);
          return (
            c.dialCode === prefillCountryCode && c.iso2 === prefillCountryISO
          );
        });
        if (countryData.length) {
          setCountryCode({
            dialCode: prefillCountryCode,
            country: prefillCountryISO as CountryIso2,
          });
          setActiveTab('SMS');
        }
      } else {
        setEmail(prefillData);
      }
    }
  }, [prefillData, prefillCountryCode]);

  // For native error listener
  useEffect(() => {
    if (typeof document === 'object') {
      storageListener(STORAGE_KEYS.LOGIN_ERROR, (error: string) => {
        setError(error);
      });
    }
  }, []);
  // Return Form Fields based on Active Tab
  const returnFormFields = () => {
    if (activeTab === 'Email') {
      return (
        <div className="form-group">
          <LabelTextInput
            placeholder="Enter your email"
            inputType={'email'}
            inputName={'email'}
            value={email}
            label="Email"
            className={`input-field ${errorMessage ? 'is-invalid' : ''}`}
            onChange={setEmail}
            disabled={isProcessing || continueEmail}
            required
            showLabelOnFocus
            alwaysShowLabel={continueEmail}
            inputRef={inputRef}
          />
        </div>
      );
    }
    return (
      <>
        <div className="form-group auth__phone">
          <CountrySelector
            selectedCountry={countryCode.country}
            className="auth__phone-country"
            onSelect={({ dialCode, iso2: country }) => {
              setCountryCode({ dialCode, country });
            }}
            disabled={isProcessing || continueEmail}
            countries={countries}
          />
          <div className="auth__phone-country__dialcode">
            +{countryCode.dialCode}
          </div>
          <LabelTextInput
            placeholder="(201) 555-0123"
            inputType={'text'}
            inputName={'phoneNumber'}
            value={phoneNumber}
            className={`auth__phone-input ${errorMessage ? 'is-invalid' : ''}`}
            disabled={isProcessing || continueEmail}
            onChange={(val) => {
              setPhoneNumber(
                onlyNumbers({
                  value: val,
                  oldValue: phoneNumber,
                  max: 13,
                  compareLength: true,
                })
              );
            }}
            required
          />
        </div>

        <p className="sl-auth__sms">
          By clicking <strong>'Get a Code'</strong> you consent to receive a
          verification SMS at the provided number. Standard rates may apply.
          Your number is used solely for this login verification.
        </p>
      </>
    );
  };

  // Check IF Get Code button needs to be disabled
  const isOTPDisabled = () => {
    return (
      isProcessing ||
      ((email.length <= 0 || !validateEmail(email)) && activeTab === 'Email') ||
      (!phone(phoneNumber, {
        country: countryCode.country,
      }).isValid &&
        activeTab === 'SMS')
    );
  };

  // Updating verifyOTP to show OTP Screen
  const updateVerifyOTP = () => {
    if (activeTab === 'Email') {
      if (validateEmail(email)) {
        setVerifyOTP(true);
        setError('');
      } else {
        setError('The email must be a valid email address.');
      }
    } else if (
      phone(phoneNumber, {
        country: countryCode.country,
      }).isValid
    ) {
      setVerifyOTP(true);
      setError('');
    } else {
      setError('The number must be a valid phone number.');
    }
  };

  const tabSwitchHandler = (tab: string) => {
    setActiveTab(tab as TabSwitchDefaultTabs['tabs']);
    setError(null);
  };

  // Return VerifyOTP Screen or Login Screen
  const returnWrapper = () => {
    const returnSuccessHandler = () => {
      if (overrideSubmit) {
        return (AddMethod) => {
          onSubmit(
            AddMethod,
            (err) => {
              setError(err);
              setIsProcessing(false);
              setGoogleProcessing(false);
              setMainLoading(false);
            },
            () => {
              setIsProcessing(false);
            }
          );
        };
      } else {
        return () => {};
      }
    };

    if (verifyOTP) {
      return (
        <VerifyOTP
          sentTo={
            activeTab === 'Email'
              ? email
              : `+${countryCode.dialCode} ${phoneNumber}`
          }
          isMobileNumber={activeTab !== 'Email'}
          countryCode={countryCode.dialCode}
          countryISO={countryCode.country}
          verifyFor="login"
          isProcesssing={isProcessing}
          setIsProcessing={setIsProcessing}
          onSuccess={returnSuccessHandler()}
          overrideSubmit={overrideSubmit}
          setVerifyOTP={setVerifyOTP}
          error={error}
          setError={setError}
          onClose={onClose}
        />
      );
    }

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          updateVerifyOTP();
        }}
      >
        <div className="auth-wrapper">
          <svg
            width="31"
            height="25"
            viewBox="0 0 31 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.7253 22.3861C22.2847 22.4881 22.1079 22.5167 21.2947 22.8077C21.1179 22.8716 19.8858 22.5425 19.709 22.5602C19.7852 22.9817 18.3124 23.6495 17.9371 23.8127C18.0744 23.7528 17.8405 24.1717 18.6007 24.3865C19.1079 24.4491 20.7113 24.2478 20.9833 24.2152C21.7353 24.1254 22.7566 24.071 23.1605 24.1921C23.5644 24.3131 23.5984 24.0003 23.5984 24.0003C23.5984 24.0003 24.024 24.0982 24.0349 23.7651C24.0349 23.7651 24.3586 23.4659 24.2416 23.2483C24.1233 23.0293 23.1673 22.2814 22.7267 22.3847L22.7253 22.3861Z"
              fill="#ECECEC"
            />
            <path
              d="M10.4114 22.438C12.8171 24.9307 20.1253 23.889 20.1253 23.889C20.1253 23.889 21.1534 22.7113 22.0849 20.6048C22.4154 19.8569 22.6085 19.2095 22.7254 18.6928C22.8029 18.3474 22.8233 17.9951 22.8152 17.6416C22.8084 17.3641 22.8533 17.0786 22.8941 16.8297C22.9593 16.4353 23.0681 15.9512 23.3619 15.6561C23.3578 15.5065 23.6556 14.9965 23.8514 14.6756C23.9507 14.5151 24.0255 14.4009 24.0255 14.4009C24.0255 14.4009 24.4172 14.8401 24.4498 15.2128C24.9448 15.5826 25.4548 15.8302 25.622 15.9117C25.671 15.856 25.724 15.7975 25.7798 15.7363C26.287 15.1869 26.3836 14.1792 26.3578 13.4217C26.3414 12.9757 26.2843 12.6167 26.2408 12.4888C26.1266 12.1434 26.4257 11.2921 26.4475 10.1865C26.472 9.0809 25.758 8.06777 25.7336 7.99841C25.1134 6.20061 20.4312 5.51113 19.1516 6.10677C17.8719 6.70241 16.104 9.67247 16.104 9.67247C15.9041 9.76358 15.6906 9.84653 15.5152 9.99476C9.67433 14.9285 8.59456 20.5572 10.41 22.438H10.4114Z"
              fill="#ECECEC"
            />
            <path
              d="M24.1234 2.53993C24.1234 2.53993 24.6986 1.69951 25.0971 0.931159C25.4955 0.164168 25.7457 0.000976562 25.7457 0.000976562L25.6424 0.472867L26.0109 0.251202C26.0109 0.251202 25.8491 0.354555 25.8777 0.679574C25.9062 1.00459 26.1578 1.72671 25.9511 2.34683C25.7444 2.96695 25.4792 3.7489 25.4792 3.7489C25.4792 3.7489 25.7661 3.97736 25.6709 4.3323C25.6709 4.3323 25.9987 4.75523 25.8763 5.20537C25.7539 5.6555 25.7675 5.69629 25.8355 6.13419C25.9035 6.57072 27.1193 8.88121 24.7557 10.063C22.3936 11.2447 19.1189 9.11648 19.1787 7.69537C19.2386 6.27426 19.9484 4.40846 20.2748 3.99368C20.6012 3.57891 20.2748 3.19405 20.0681 2.51274C19.8614 1.83142 20.317 0.336877 20.4217 0.232164L20.4421 0.758449L20.6733 0.442948C20.6733 0.442948 20.6107 0.800606 20.7576 1.05355C20.9044 1.30649 21.4878 1.46288 21.9298 2.55626C21.9298 2.55626 23.2571 2.24211 24.122 2.54266L24.1234 2.53993Z"
              fill="#ECECEC"
            />
            <path
              d="M23.3388 13.5821C23.3565 13.986 23.5659 14.3559 23.8515 14.6755C24.0296 14.8754 24.2377 15.0536 24.4499 15.2127C24.9449 15.5826 25.4548 15.8301 25.6221 15.9117C25.6384 15.9198 25.6507 15.9266 25.6588 15.9307C25.8846 16.0436 25.8002 16.3564 25.8274 16.6664C25.856 16.9629 26.2422 16.5903 26.2776 16.5563C26.264 16.5848 26.2477 16.9887 26.4503 17.035C26.5156 17.0499 26.5618 17.0526 26.5944 17.035C26.608 17.0499 26.6244 17.0594 26.6475 17.0622C26.846 17.0907 27.7504 16.015 27.0432 15.1379C26.7059 14.7177 26.4911 14.0268 26.3564 13.4217C26.2137 12.7594 26.1661 12.1964 26.1661 12.1964C25.8261 11.885 23.2803 12.2821 23.3374 13.5821H23.3388Z"
              fill="#ECECEC"
            />
            <path
              d="M20.8069 13.5944C20.7919 13.9983 20.9714 14.3832 21.2298 14.7259C21.3916 14.9394 21.5847 15.1338 21.7819 15.3093C22.2457 15.7172 22.7339 16.0055 22.893 16.1007C22.9079 16.1102 22.9202 16.117 22.9283 16.1225C23.1446 16.253 23.2615 16.6529 23.2642 16.9643C23.2683 17.2621 23.524 17.0703 23.5621 17.0391C23.5471 17.0663 23.5049 17.1941 23.6001 17.2403C23.7239 17.3002 23.8055 17.2648 23.8395 17.2526C23.8517 17.2675 23.9659 17.1207 23.9891 17.1247C24.1835 17.1683 25.0076 16.3768 24.3739 15.4452C24.072 14.9992 23.9129 14.2934 23.8272 13.6787C23.7375 13.0069 23.7361 12.4426 23.7361 12.4426C23.422 12.1053 20.8531 12.2943 20.8055 13.5958L20.8069 13.5944Z"
              fill="#ECECEC"
            />
            <path
              d="M0.900168 7.97008L0.913769 7.98504C1.38974 6.64145 2.20025 5.93973 2.20025 5.93973L1.25102 6.41435C2.55518 3.68772 4.53113 3.41029 4.53113 3.41029L3.46361 3.37086C6.46766 1.3949 9.90552 3.56805 9.90552 3.56805L9.19428 2.93568C11.9209 3.21175 12.7518 6.17636 12.7518 6.17636L12.5941 4.91164C14.3728 6.92703 12.4363 11.393 12.4363 11.393L13.1353 9.81685C13.1965 9.9882 13.4155 10.5036 12.794 11.4392C10.6358 14.6894 11.9712 17.1277 12.08 17.1645C12.08 17.1645 14.8869 21.7487 11.0519 21.6304C10.7568 21.6209 10.4767 21.5937 10.2088 21.5529C9.34251 21.6155 8.19746 21.4618 7.19928 20.5384C7.19928 20.5384 7.36928 20.5466 7.6467 20.567C6.29494 19.6613 5.71698 18.5081 5.71698 18.5081L6.54653 19.0221C4.72832 16.4532 6.03656 12.8141 6.27455 11.9845C6.51117 11.155 5.66395 11.461 5.66395 11.461C5.83801 11.1115 6.38878 11.1972 6.38878 11.1972C5.54291 10.8137 4.1354 12.4619 4.1354 12.4619L4.4645 11.7017C4.2306 11.9655 4.01845 12.33 3.83622 12.7094C3.39969 14.0761 4.07013 14.8608 4.07013 14.8608C3.67167 14.7751 3.31673 14.6622 2.99851 14.5262C2.77549 14.4678 2.33215 14.3018 1.88338 13.8463C-0.480146 11.8254 0.901525 7.97144 0.901525 7.97144L0.900168 7.97008Z"
              fill="#ECECEC"
            />
            <path
              d="M25.5159 16.7087C25.5469 16.7235 25.5836 16.7388 25.6233 16.7544C26.2227 17.0034 27.633 17.4424 28.7334 16.6752C28.7363 16.6755 28.7398 16.6699 28.743 16.6673C28.7589 16.6542 28.7775 16.6442 28.7935 16.631C28.7999 16.6258 28.8063 16.6205 28.8127 16.6152C28.8348 16.5997 28.8571 16.5813 28.8795 16.5629C28.9466 16.5076 29.0079 16.4517 29.0662 16.3956C29.2084 16.5064 29.3923 16.5212 29.5137 16.4212L29.6766 16.2869C29.7405 16.2343 29.7991 16.1752 29.8522 16.1127C29.8525 16.1098 29.8586 16.1075 29.8589 16.1045C29.8624 16.099 29.8687 16.0937 29.8722 16.0882C29.8757 16.0826 29.8789 16.08 29.8824 16.0745C29.8858 16.0689 29.8893 16.0634 29.8928 16.0578C29.8963 16.0523 29.8997 16.0467 29.9032 16.0412C29.9099 16.033 29.9137 16.0245 29.92 16.0193C29.9235 16.0137 29.9302 16.0056 29.9337 16C29.9342 15.9942 29.9403 15.9918 29.9438 15.9863C29.9473 15.9807 29.9508 15.9752 29.9542 15.9696C29.9548 15.9638 29.9612 15.9586 29.9644 15.9559C29.9649 15.9501 29.9713 15.9448 29.9748 15.9393C29.9754 15.9335 29.9818 15.9282 29.9823 15.9224C29.9829 15.9165 29.9893 15.9113 29.9928 15.9057C29.9933 15.8999 29.9997 15.8946 30.0003 15.8888C30.0008 15.883 30.0072 15.8777 30.0107 15.8722C30.0113 15.8663 30.0147 15.8608 30.0182 15.8553C30.0188 15.8494 30.0252 15.8442 30.0257 15.8383C30.0263 15.8325 30.0298 15.827 30.0332 15.8214C30.0338 15.8156 30.0402 15.8103 30.041 15.8016C30.0416 15.7958 30.0451 15.7902 30.0483 15.7876C30.0488 15.7817 30.0526 15.7733 30.0561 15.7677C30.0566 15.7619 30.0598 15.7593 30.0633 15.7537C30.0641 15.745 30.0708 15.7368 30.0717 15.7281C30.0719 15.7252 30.0722 15.7223 30.0757 15.7167C30.0797 15.7053 30.0838 15.694 30.0878 15.6826C30.0881 15.6797 30.0887 15.6738 30.0919 15.6712C30.0927 15.6625 30.0962 15.6569 30.0999 15.6485C30.1005 15.6426 30.1008 15.6397 30.1042 15.6342C30.1048 15.6284 30.1086 15.6199 30.1091 15.6141C30.1097 15.6082 30.1103 15.6024 30.1105 15.5995C30.1111 15.5937 30.1119 15.5849 30.1154 15.5794C30.116 15.5736 30.1165 15.5677 30.1171 15.5619C30.1177 15.5561 30.1185 15.5473 30.122 15.5418C30.1225 15.536 30.1231 15.5301 30.1237 15.5243C30.1242 15.5185 30.1251 15.5097 30.1256 15.5039C30.1262 15.4981 30.1268 15.4923 30.127 15.4893C30.1279 15.4806 30.1285 15.4748 30.1293 15.466C30.1299 15.4602 30.1301 15.4573 30.1307 15.4515C30.1315 15.4427 30.1324 15.434 30.1332 15.4252C30.1335 15.4223 30.1341 15.4165 30.1344 15.4136C30.1355 15.4019 30.1363 15.3932 30.1375 15.3815C30.1375 15.3815 30.1377 15.3786 30.138 15.3757C30.1391 15.3641 30.1405 15.3495 30.1417 15.3378C30.1417 15.3378 30.142 15.3349 30.1422 15.332C30.1434 15.3204 30.1445 15.3087 30.1456 15.2971C30.1459 15.2941 30.1462 15.2912 30.1467 15.2854C30.1476 15.2767 30.1484 15.2679 30.1495 15.2563C30.1498 15.2533 30.1504 15.2475 30.1507 15.2446C30.1515 15.2359 30.1524 15.2271 30.1532 15.2184C30.1535 15.2155 30.154 15.2096 30.1543 15.2067C30.1552 15.198 30.156 15.1892 30.1569 15.1805C30.1571 15.1776 30.1577 15.1718 30.158 15.1689C30.1588 15.1601 30.1597 15.1514 30.1605 15.1426C30.1608 15.1397 30.1614 15.1339 30.1616 15.131C30.1625 15.1222 30.1636 15.1106 30.1615 15.1016C30.1618 15.0986 30.1621 15.0957 30.1624 15.0928C30.1635 15.0812 30.1617 15.0692 30.1599 15.0573C30.1599 15.0573 30.1602 15.0544 30.1605 15.0515C30.1571 14.9953 30.1505 14.9417 30.1442 14.8852C30.1442 14.8852 30.1442 14.8852 30.1445 14.8823C30.1427 14.8704 30.1409 14.8584 30.1391 14.8465C30.1391 14.8465 30.1394 14.8436 30.1397 14.8407C30.1379 14.8287 30.1361 14.8168 30.1343 14.8049C30.1343 14.8049 30.1346 14.8019 30.1349 14.799C30.1331 14.7871 30.1284 14.7749 30.1266 14.7629C30.1266 14.7629 30.1266 14.7629 30.1269 14.76C30.1251 14.7481 30.1207 14.733 30.1189 14.721C30.0474 14.4259 29.9051 14.1328 29.6921 13.8711L29.4084 13.5203C29.4084 13.5203 29.4084 13.5203 29.4087 13.5174C29.4586 13.4575 29.5117 13.395 29.5642 13.3383C29.6367 13.2571 29.7148 13.1793 29.7955 13.1048C29.8211 13.0837 29.8437 13.0624 29.8693 13.0413C30.057 13.0771 30.2418 13.1126 30.4297 13.1454C30.3852 13.0588 30.3496 12.97 30.3266 12.8737C30.303 12.7832 30.2916 12.688 30.2918 12.5939C30.2921 12.591 30.2924 12.5881 30.2927 12.5852C30.0657 12.6515 29.8334 12.682 29.5983 12.6799C29.5012 12.894 29.3621 13.0865 29.1934 13.2496L28.8936 12.8825C28.8936 12.8825 28.8857 12.8729 28.8804 12.8665C28.8696 12.8566 28.862 12.8441 28.8512 12.8343C28.8459 12.8279 28.838 12.8183 28.8328 12.8119C28.8193 12.7988 28.8091 12.7831 28.7956 12.7701C28.7875 12.7634 28.7825 12.7541 28.7743 12.7474C28.7635 12.7376 28.7527 12.7277 28.7451 12.7152C28.7372 12.7056 28.7264 12.6957 28.7182 12.6891C28.7103 12.6795 28.6995 12.6696 28.6913 12.663C28.6831 12.6563 28.6778 12.6499 28.6697 12.6432C28.6562 12.6302 28.6425 12.62 28.6291 12.607C28.6209 12.6003 28.6127 12.5936 28.6074 12.5872C28.5937 12.5771 28.5829 12.5672 28.5692 12.5571C28.561 12.5504 28.5528 12.5437 28.5446 12.5371C28.5335 12.5301 28.5227 12.5202 28.5146 12.5136C28.5064 12.5069 28.4982 12.5002 28.49 12.4936C28.4763 12.4834 28.4626 12.4733 28.4486 12.466C28.4404 12.4594 28.4319 12.4556 28.4267 12.4492C28.4129 12.4391 28.3963 12.4286 28.3826 12.4185C28.3741 12.4147 28.3689 12.4083 28.3604 12.4046C28.3493 12.3976 28.3353 12.3904 28.3242 12.3835C28.3157 12.3797 28.3076 12.373 28.2991 12.3693C28.2851 12.362 28.2711 12.3548 28.2574 12.3447C28.2489 12.3409 28.2434 12.3374 28.2349 12.3337C28.218 12.3261 28.2043 12.316 28.1873 12.3085C28.1815 12.3079 28.1733 12.3013 28.1678 12.2978C28.1538 12.2905 28.1398 12.2833 28.1255 12.279C28.1197 12.2784 28.1115 12.2718 28.1059 12.2683C28.0919 12.261 28.0747 12.2564 28.0607 12.2492C28.0549 12.2487 28.0494 12.2452 28.0409 12.2414C28.024 12.2339 28.0068 12.2293 27.9899 12.2218C27.984 12.2212 27.9785 12.2178 27.9729 12.2143C27.956 12.2068 27.9359 12.2019 27.9187 12.1973C27.9158 12.197 27.91 12.1964 27.9071 12.1962C27.8899 12.1916 27.8727 12.1869 27.8584 12.1826C27.8525 12.1821 27.8496 12.1818 27.8438 12.1812C27.8237 12.1763 27.8065 12.1717 27.7893 12.1671C27.7835 12.1666 27.7776 12.166 27.7747 12.1657C27.7543 12.1638 27.7374 12.1563 27.717 12.1543C27.7141 12.154 27.7112 12.1537 27.7054 12.1532C27.6879 12.1515 27.6678 12.1466 27.6503 12.1449C27.6474 12.1446 27.6445 12.1443 27.6415 12.1441C27.6211 12.1421 27.6008 12.1401 27.5833 12.1384C27.5804 12.1382 27.5745 12.1376 27.5716 12.1373C27.5512 12.1353 27.5308 12.1334 27.5133 12.1317C27.5104 12.1314 27.5075 12.1311 27.5046 12.1308C27.4842 12.1289 27.4638 12.1269 27.4431 12.1279C27.4227 12.1259 27.4021 12.1268 27.3814 12.1278C27.3785 12.1275 27.3756 12.1272 27.3726 12.1269C27.3523 12.125 27.3313 12.1288 27.3135 12.13C27.3135 12.13 27.3077 12.1295 27.3048 12.1292C27.2634 12.1311 27.2218 12.1359 27.1799 12.1436C27.1799 12.1436 27.177 12.1433 27.1741 12.1431C27.1531 12.1469 27.1351 12.1511 27.1141 12.1549L27.1083 12.1543C27.0873 12.1582 27.0693 12.1623 27.048 12.1691C27.0271 12.173 27.0088 12.18 26.9875 12.1868C26.9875 12.1868 26.9846 12.1865 26.9817 12.1862C26.9634 12.1933 26.9424 12.1972 26.9241 12.2042C26.9241 12.2042 26.9183 12.2036 26.9151 12.2063C26.8967 12.2133 26.8784 12.2204 26.8601 12.2274C26.7437 12.275 26.6315 12.3407 26.5293 12.4249L26.3664 12.5591C26.245 12.6592 26.2214 12.8422 26.303 13.0029C26.237 13.0466 26.1705 13.096 26.1034 13.1513C25.0497 14.0142 25.0798 15.3169 25.2053 16.0583C25.2053 16.0583 25.2053 16.0583 25.205 16.0612C25.205 16.0612 25.205 16.0612 25.2047 16.0641C25.227 16.1986 25.2539 16.3159 25.2775 16.4064C25.2113 16.4529 25.1477 16.5026 25.0814 16.5492C25.0085 16.6039 24.9327 16.6583 24.8597 16.713C24.9706 16.8443 25.0814 16.9756 25.1926 17.1039C25.2777 16.9533 25.3902 16.8231 25.5241 16.7154L25.5159 16.7087Z"
              fill="#EB6F19"
            />
          </svg>

          <h1 className="auth-login"> Sign Up</h1>
        </div>
        <p className="auth__message-otp">
          {!editLoginMethod ? (
            <>
              <strong>
                {overrideSubmit
                  ? Account_Message.login
                  : Account_Message.signIn}
              </strong>{' '}
              easily with a one-time code.
            </>
          ) : (
            'Please enter your new email address.'
          )}
        </p>
        {!editLoginMethod && !continueEmail && !user.phone && (
          <TabSwitch onChange={tabSwitchHandler} activeTab={activeTab} />
        )}
        {returnFormFields()}
        <div className="form-group form-group__action">
          <button
            type="submit"
            className={`button sl-auth__register-button email__button ${
              isOTPDisabled() ? 'button__secondary' : 'button__primary'
            }`}
            disabled={isOTPDisabled()}
            onClick={updateVerifyOTP}
          >
            {Account_Message.codeLabel}
          </button>
        </div>
        {onClose && (
          <button className="login-popup__container__close-icon">
            <Close
              onClick={() => {
                onClose();
              }}
            />
          </button>
        )}
        {error && (
          <div className="sl-auth__error otp-error">
            <span className="otp-error__icon">
              <ExclamationSquare />
            </span>
            <span>{error}</span>
          </div>
        )}
        {!editLoginMethod && !continueEmail && (
          <>
            <div className="sl-auth__or">
              <span className="sl-auth__or-label">Or</span>
            </div>
            <GoogleButton
              processing={googleProcessing}
              label={Account_Message.googleButtonLabel}
              setProcessing={setGoogleProcessing}
              registerRedirect={registerRedirect}
              setMainLoading={setMainLoading}
              overrideSubmit={overrideSubmit}
              onSuccess={returnSuccessHandler()}
            />
            <FacebookButton
              text={Account_Message.facebookButtonLabel}
              registerRedirect={registerRedirect}
              setMainLoading={setMainLoading}
              overrideSubmit={overrideSubmit}
              onSuccess={returnSuccessHandler()}
            />
            <AppleButton
              text={Account_Message.appleButtonLabel}
              registerRedirect={registerRedirect}
              setMainLoading={setMainLoading}
              overrideSubmit={overrideSubmit}
              onSuccess={returnSuccessHandler()}
            />
          </>
        )}
      </form>
    );
  };

  return (
    <AuthHeaderLayout
      title={overrideSubmit ? Account_Message.login : Account_Message.signIn}
      helmetTitle="Scrappi"
    >
      <>
        {(googleProcessing || isProcessing || loading) && (
          <div className="auth__loading">
            <FontAwesomeIcon icon={faSpinnerIcon} spin size="lg" />
          </div>
        )}
        {errorMessage && (
          <span role="alert" className="sl-auth__error-wrapper">
            {errorMessage}
          </span>
        )}
        {returnWrapper()}
      </>
    </AuthHeaderLayout>
  );
};

export default Register;
